import { AsyncPipe } from '@angular/common';
import { Component, inject, type OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';

import { type DeviceAdditionalAttribute, type DeviceData } from '../../models';
import { DevicesActions, DevicesSelectors } from '../../ngrx-store';

export interface Attribute {
  attributeType: string;
  friendlyName: string;
  title: string;
  value: string;
}

@Component({
  imports: [
    MatDialogModule,
    AsyncPipe,
    FormsModule,
    MatError,
    MatFormField,
    MatInput,
    MatProgressSpinner,
    ReactiveFormsModule,
  ],
  selector: 'avs-edit-data-point-dialog',
  templateUrl: './edit-data-point-dialog.component.html',
})
export class EditDataPointDialogComponent implements OnDestroy {
  data = inject<{
    attribute: Attribute;
    device: DeviceData | undefined;
  }>(MAT_DIALOG_DATA);

  protected form: FormGroup;
  protected isLoading$: Observable<boolean> = of(false);
  private subscriptions = new Subscription();

  private dialogRef = inject(MatDialogRef<EditDataPointDialogComponent>);
  private fb = inject(FormBuilder);
  private store = inject(Store);

  constructor() {
    this.isLoading$ = this.store.select(DevicesSelectors.isDeviceUpdating);
    this.form = this.fb.group({
      attributeType: [{ disabled: true, value: this.data.attribute.attributeType }],
      friendlyName: [this.data.attribute.friendlyName],
      name: [{ disabled: true, value: this.data.attribute.title }],
    });
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }

  protected saveChanges() {
    const additionalAttributes = this.data.device?.additionalAttributes;

    if (!additionalAttributes) {
      return;
    }
    const attributeIndex = additionalAttributes.findIndex(attr => attr.name === this.data.attribute.title);

    if (attributeIndex === -1) {
      return;
    }
    const updatedAttributes = this.getUpdatedAttributes(additionalAttributes, attributeIndex);

    this.dispatchUpdate(updatedAttributes);
  }

  private getUpdatedAttributes(attributes: DeviceAdditionalAttribute[], index: number) {
    const updatedAttribute = { ...attributes[index], friendlyName: this.form.value.friendlyName };

    return [...attributes.slice(0, index), updatedAttribute, ...attributes.slice(index + 1)];
  }

  private dispatchUpdate(updatedAttributes: DeviceAdditionalAttribute[]) {
    const { id: deviceId, location } = this.data.device!;

    this.store.dispatch(
      DevicesActions.updateDeviceData({
        data: { additionalAttributes: updatedAttributes },
        deviceId,
        locationId: location.id,
      }),
    );
    this.subscriptions.add(
      this.store.select(DevicesSelectors.isDeviceUpdating).subscribe(updating => {
        if (!updating) {
          this.dialogRef.close();
        }
      }),
    );
  }
}
